<script setup lang="ts">
type Props = {
  selectedDate?: string;
  id: string;
  label?: string;
  required: boolean;
  name: string;
  disabled: boolean;
  initialValue?: string;
  separateFields?: boolean;
};

type Emits = {
  'update:selectedDate': [string];
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const days = ref(Array.from({ length: 31 }, (_, i) => i + 1));
const years = ref(
  Array.from({ length: 99 }, (_, i) => new Date().getFullYear() - 18 - i),
);

const selectedDay = ref<any>(0);
const selectedMonth = ref<any>(0);
const selectedYear = ref<any>(0);

watchEffect(() => {
  selectedYear.value = Number((props.initialValue ?? '').slice(0, 4)) || 0;
});

watchEffect(() => {
  selectedMonth.value = Number((props.initialValue ?? '').slice(5, 7)) || 0;
});

watchEffect(() => {
  selectedDay.value = Number((props.initialValue ?? '').slice(8, 10)) || 0;
});

const daysInSelectedMonth = computed(() => {
  const monthIndex = selectedMonth.value - 1;
  const maxDay = new Date(selectedYear.value, monthIndex + 1, 0).getDate();

  return days.value.slice(0, maxDay);
});

const filteredYears = computed(() => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const yearMin = currentYear - 99;
  let yearMax = currentYear - 18;

  if (currentMonth >= selectedMonth.value && currentDay >= selectedDay.value) {
    yearMax += 1;
  }

  return years.value.filter((ano) => ano >= yearMin && ano <= yearMax);
});

watchEffect(() => {
  if (+selectedDay.value && +selectedMonth.value && +selectedYear.value) {
    const month = selectedMonth.value.toString().padStart(2, '0');
    const day = selectedDay.value.toString().padStart(2, '0');
    const formattedDate = `${selectedYear.value}-${month}-${day}`;

    emit('update:selectedDate', formattedDate);
  } else {
    emit('update:selectedDate', '');
  }
});

const updateDay = () => {
  if (
    selectedDay.value &&
    selectedMonth.value &&
    !daysInSelectedMonth.value.includes(selectedDay.value)
  ) {
    const lastDayInMonth = daysInSelectedMonth.value.slice(-1);

    selectedDay.value = String(lastDayInMonth).padStart(2, '0');
  }
};
</script>

<template>
  <div class="input-group">
    <div
      :class="[`group`, { [`separateFields`]: props.separateFields }]"
      :for="`${props.id}_year` || 'id'"
    >
      <div class="selectGroup">
        <select
          :id="`${props.id}_year`"
          v-model="selectedYear"
          :class="['input', { 'mt-3': props.label }]"
          :disabled="props.disabled || false"
          @change="updateDay"
        >
          <option value="0">
            {{ props.separateFields ? $t('general.year') : `---` }}
          </option>
          <option
            v-for="year in filteredYears"
            :key="year"
            :value="year"
          >
            {{ year }}
          </option>
        </select>
        <select
          :id="`${props.id}_month`"
          v-model="selectedMonth"
          :class="['input', { 'mt-3': props.label }]"
          :disabled="props.disabled"
          @change="updateDay"
        >
          <option value="0">
            {{ props.separateFields ? $t('general.month') : `---` }}
          </option>
          <option
            v-for="i in 12"
            :key="i"
            :value="i"
          >
            {{ $t(`dateTime.shortMonths[${i - 1}]`) }}
          </option>
        </select>
        <select
          :id="`${props.id}_day`"
          v-model="selectedDay"
          :class="['input', { 'mt-3': props.label }]"
          :disabled="props.disabled"
        >
          <option value="0">
            {{ props.separateFields ? $t('general.day') : `---` }}
          </option>
          <option
            v-for="day in daysInSelectedMonth"
            :key="day"
            :value="day"
          >
            {{ day }}
          </option>
        </select>
      </div>
      <span
        v-if="props.label"
        class="label"
      >
        {{ props.label || 'Label' }}
        <small
          v-if="props.required"
          class="required"
          >*</small
        >
      </span>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.input-group {
  @apply flex flex-wrap relative;
}

.group {
  @apply relative block overflow-hidden rounded-md px-4 pt-3 h-[46px] sm:h-[50px] flex-grow;
  @apply cursor-text border-0 bg-auth-bg-inputs border-auth-bg-inputs;
}

.selectGroup {
  @apply w-full flex items-center;
}

.input {
  @apply h-5 mt-1 w-[70px] pr-4 border-none bg-auth-bg-inputs p-0 placeholder-transparent text-auth-text-inputs;
  @apply focus:border-transparent focus:outline-none focus:ring-0 text-sm;
  option {
    /* ??? */
    @apply bg-auth-bg-inputs;
  }
}
.input:-webkit-autofill,
.input:-webkit-autofill:hover,
.input:-webkit-autofill:focus {
  -webkit-appearance: none !important;
  -webkit-text-fill-color: #fff !important;
  @apply shadow-input shadow-auth-bg-inputs !important;
}

.label {
  @apply absolute start-4 top-[1.5em] -translate-y-1/2 text-[.6em]  text-auth-text-inputs;
  @apply peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-[1.5em];
  @apply opacity-80 peer-focus:text-[.6em] peer-focus:opacity-60 cursor-text select-none;
}
.required {
  @apply font-bold text-opacity-70 text-sm text-error;
}

.group[disabled]:not([disabled='false']),
.input[disabled]:not([disabled='false']),
.group[disabled]:not([disabled='false']) .label {
  @apply opacity-40 cursor-not-allowed;
}

.group.separateFields {
  @apply bg-transparent p-0;

  .selectGroup {
    @apply gap-3 flex-row-reverse;
  }

  .input {
    @apply flex-1 relative block rounded-md px-4 h-[46px] sm:h-[50px] m-0 pt-0 border-0 bg-auth-bg-inputs border-auth-bg-inputs;
  }
}
</style>
